import axios from "axios";
import isEmail from 'is-email';
import _ from 'lodash';
import Vue from 'vue';

const route_prefix = '/admin/account-details';

const ACCOUNT_USER_BASE_URL = '/admin/menuclick';

const buildFormData = (params) => {
  const formData = new FormData();

  for(const prop in params) {
    formData.append(prop, params[prop]);
  }

  return formData;
}

function failureNotification (title, message) {
    Vue.notify({
        group: 'admin:account:details',
        title: title,
        text: message,
        type: 'error',
        duration: 3000,
        speed: 500
    });
}


const successNotification = (message) => {
  Vue.notify({
    group: 'admin:account:details',
    title: '',
    text: message,
    type: 'success',
    duration: 3000,
    speed: 500
  });
};

export default {
    namespaced: true,
    state: {
        accountId: 0,

        account: {},

        users: {
          users: [],
        },

        emailPreferences: {
            notifications_active: true,
            method: 'cc',
            emails: []
        },

        security: {
            mfaEnabled: false
        },

        programSettings: {
            programsEnabled: false,
            programsVisibleToClients: false,
            programsSchedulerEnabled: false
        },

        translationReviewManagment: {
            isEnabled: false
        },

        kibanaReports: {
            volume: true,
            spend: false,
            username: '',
            password: '',
        }
    },
    mutations: {

        /**
         * Set account data
         *
         * @param {Object} state
         * @param {Object} account
         */
        setAccount (state, account) {
            state.account = account;
        },

        /**
         * set account id
         */
        setAccountId (state, accountId) {
          state.accountId = accountId;
        },

        /**
         * Update email preferences in the store state.
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setEmailPreferences(state, payload) {
            // Update the sort order of the teams
            state.emailPreferences.method = payload.method;
            state.emailPreferences.emails = payload.emails.filter(Boolean);
        },

        /**
         * sets a users password state (activated, reset)
         *
         * @param {Object} state
         * @param {Object} payload { userId, passwdstate }
         */
        setUserPasswordState(state, payload) {
            const {userId, passwdstate} = payload;
            let user = state.users.users.find(user => user.id == userId);

            if (user) {
                user.passwdstate = passwdstate;
            } else {
                console.error('setUserPasswordState: no user found')
            }
        },

        /**
         * Set mfa settings in store state
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setSecuritySettings (state, payload) {
            state.security.mfaEnabled = payload.mfaEnabled;
        },

        /**
         * Set state of account programs (enabled or disabled )
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setProgramsEnabledState (state, payload) {
            state.programSettings.programsEnabled = payload.programsEnabled;
        },

        /**
         * Set state of account programs (enabled or disabled )
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setProgramsVisibilityState (state, payload) {
            state.programSettings.programsVisibleToClients = payload.programsVisibleToClients;
        },

        /**
         * Set program scheduler visibility in state
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setProgramSchedulerVisibilityState (state, payload) {
            state.programSettings.programsSchedulerEnabled = payload.programsSchedulerEnabled;
        },

        /**
         * Set translation review status for users of account
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setTranslationReviewStatus (state, payload) {
            state.translationReviewManagment.isEnabled = payload.isEnabled;
        },

        /**
         * Set kibana reports settings state for account
         *
         * @param {Object} state
         * @param {Object} payload
         */
        setReportsSettings (state, payload) {
            state.kibanaReports = Object.assign(state.kibanaReports, payload);
        },

        /**
         * Set account users
         *
         * @param state
         * @param users
         */
        setAccountUsers (state, users) {
            state.users.users = users;
        },

        /**
         * Add a new email to the list of emails to forward
         *
         * @param {Object} state
         * @param {Stirng} email
         */
        pushForwardingEmail (state, email) {
            //check that email doesn't exist already
            if (state.emailPreferences.emails.indexOf(email) > -1) {
                return;
            }

            //check that it's an email
            if (!isEmail(email)) return;

            state.emailPreferences.emails.push(email);
        },


        /**
         * remove a forward email from the list
         *
         * @param {Object} state
         * @param {Stirng} email
         */
        deleteForwardingEmail (state, email) {
            let index = state.emailPreferences.emails.findIndex(e => e == email);

            //check if email exists
            if (index < 0) return;

            //remove email
            state.emailPreferences.emails.splice(index, 1);
        },

        /**
         * set account user role
         *
         * @param {Object} state
         * @param {Object} payload {userId, role}
         */
        setAccountUserRole (state, payload) {
            const {userId, role} = payload;
            let index = state.users.users.findIndex(u => u.id == userId);

            //check if user exists
            if (index < 0) {
                console.error('user id not found');
                failureNotification('Failed to update', 'user not found');
                return;
            }

            state.users.users[index].role = role;
        },
    },
    actions: {

        /**
         * add forward email to emails list
         *
         * @param {Object} context {state, getters, commit}
         * @param {String} email
         */
        addForwardingEmail (context, email) {
          context.commit('pushForwardingEmail', email);
        },

        /**
         * remove forward email from emails list
         *
         * @param {Object} context {state, getters, commit}
         * @param {String} email
         */
        removeForwardingEmail (context, email) {
          context.commit('deleteForwardingEmail', email);
        },

        /**
         * add forward email to emails list
         *
         * @param {Object} context {state, getters, commit}
         * @param {Number} accountId
         */
        async getAccount (context, accountId) {
          let response = await axios.get(route_prefix + `/${accountId}`);

          context.commit('setAccountId', accountId);
          context.commit('setAccount', response.data);

          return response.data;
        },

        /**
         * get mfa settings data
         *
         * @param {Object} context {state, getters, commit}
         * @param {Number} accountId
         * @return {Promise}
         */
        async loadMfaSettingsData (context, accountId) {
            try {
                const response = await axios.get(`/admin/data/account/mfa/${accountId}`);

                context.commit('setSecuritySettings', {
                    mfaEnabled: response.data.mfaEnabled,
                });

                return response.data;
            } catch ($e) {
                //handle error
                console.error('load mfa settings error:', $e.message);
            }
        },

        /**
         * get email preferences data for account
         *
         * @param {Object} context {state, getters, commit}
         * @param {Number} accountId
         * @return {Promise}
         */
        async loadEmailPreferenceData (context, accountId) {
            try {
                const response = await axios.get(`/admin/data/account/email-preferences/${accountId}`);
                const {emails, method} = response.data;

                context.commit('setEmailPreferences', {
                    emails,
                    method,
                });

                return response.data;
            } catch ($e) {
                //handle error
                console.error('load email preferences error:', $e.message);
            }
        },

        /**
         * set account users password state
         *
         * @param {Object} context {state, getters, commit}
         * @param {Number} accountId
         * @return {Promise}
         */
        setPasswordState (context, {user, passwdstate}) {
            try {
                //const {user, passwdstate} = payload;

                let params = {
                  t: `aapdw${user.id}dd`,
                  v: passwdstate,
                };

                return axios.get(ACCOUNT_USER_BASE_URL, {params})
                  .then(result => {
                    if(result.data) {
                        failureNotification('failed to update users password state');
                      console.error('Account User Error: failed to update users password state');
                    }

                    successNotification('Users password state updated');

                    context.commit('setUserPasswordState', {
                        userId: user.id,
                        passwdstate
                    })

                    return result.data;
                })
            } catch ($e) {
                console.error('set password state error:', $e.message)
            }
        },

        /**
         * get TRM settings for an account
         *
         * @param {Object} context {state, getters, commit}
         * @param {Number} accountId
         * @return {Promise}
         */
        async loadTRMPreferencesData (context, accountId) {
            try {
                const response = await axios.get(`/admin/data/account/trm-preferences/${accountId}`);
                const { isEnabled } = response.data;

                context.commit('setTranslationReviewStatus', {
                    isEnabled
                });

                return response.data;
            } catch ($e) {
                //handle error
                console.error('load email preferences error:', $e.message);
            }
        },

        /**
         * Get Account program settings data
         *
         * @param {Object} context {state, getters, commit}
         *
         * @return {Promise}
         */
        async loadProgramPreferencesData (context, accountId) {
          try {
              const response = await axios.get(`/admin/data/account/programs-preferences/${accountId}`);

              const { programsEnabled, programsVisibleToClients, programsSchedulerEnabled } = response.data;

              context.commit('setProgramsEnabledState', {programsEnabled});
              context.commit('setProgramsVisibilityState', {programsVisibleToClients});
              context.commit('setProgramSchedulerVisibilityState', {programsSchedulerEnabled});

              return response.data;

          } catch ($e) {
              //handle error
              console.error('load program prefs data error:', $e.message);
          }
        },

        /**
         * Get Kibana reports settings for account
         *
         * @param {Object} context {state, getters, commit}
         *
         * @return {Promise}
         */
        async loadKibanaReportsData (context, accountId) {
            try {
                const response = await axios.get(`/admin/data/account/reports-preferences/${accountId}`);

                const { volume, spend, username, password } = response.data;

                context.commit('setReportsSettings', { volume, spend, username, password });

                return response.data;

            } catch ($e) {
                //handle error
                console.error('load program prefs data error:', $e.message);
            }
        },

        /**
         * Update Kibana reports settings
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateKibanaReportsSettings (context, payload) {
            try {
                const accountId = context.state.accountId;
                const state = context.state.kibanaReports;

                //merge the payload and our current state for kibana reports settings
                //remove any keys that aren't currenlty in the state
                let params = _.pick(Object.assign({}, state, payload), _.keys(state));

                //transform kibana account keys into a format for our backend ex: 'password' to 'kibana-password'
                params = _.mapKeys(params, (val, key) => {
                    if (key.includes('username') || key.includes('password')) key = `kibana-${key}`;

                    return key;
                });

                const response = await axios.post(`/admin/account/${accountId}/enable-kibana-embed`, params);

                //check if the update was succesful if not keep the state as it was.
                //transform the object keys back so that we can use it in our state
                const newState = _.mapKeys(params, (val, key) => {
                    return key.includes('kibana') ? key.replace('kibana-', '') : key;
                })

                //send success notification
                successNotification("Kibana report settings updated");

                context.commit('setReportsSettings', newState);

                return response.data;
            } catch ($e) {
                console.error('update Kibana reports settings error:', $e.message);
            }
        },

        /**
         * Update Account user role
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload {userId, rolename, role}
         */
        async updateAccountUserRole (context, payload) {
            //context.commit('setAccountUserRole', payload);
            try {
                const { rolename, role, userId } = payload;

                const params = {
                  v: rolename,
                  t: `aapdr${userId}dd`,
                };

                const response = await axios.get('/admin/menuclick', {params});

                successNotification(`Updated Users role to ${rolename}`);

                context.commit('setAccountUserRole', {
                    userId,
                    role
                });

                return response.data;

            } catch ($e) {
                console.error('Update Account User Role Error', $e.message);
                failureNotification('Failed to Update Account User Role');
            }
        },

        /**
         * Update Translation review status (enabled/disabled)
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateTranslationReviewStatus (context, payload) {
            try {
                const {accountId, isEnabled} = payload;

                const params = {
                    is_review_enabled: isEnabled ? 1 : 0
                };

                //send as legacy form data
                const formData = buildFormData(params)

                const response = await axios({
                  method: 'post',
                  url: `/admin/account/${accountId}/order-pref/review`,
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                if (response.data.success === true) {
                  successNotification(`Translation Review Management now ${isEnabled ? 'enabled' : 'disabled'}`);
                } else {
                  failureNotification('Failed to update Translation Review Management');
                }

                context.commit('setTranslationReviewStatus', {
                    isEnabled: isEnabled
                });

                return response.data;
            } catch ($e) {
                console.error('update TRM status error', $e.message);
            }
        },

        /**
         * Update Programs Scheduler status
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateProgramSchedulerStatus (context, payload) {
            try {
                const params = {
                    account_id: payload.account_id,
                    status: Number(payload.status)
                };

                //send as legacy form data
                const formData = buildFormData(params)

                const response = await axios({
                  method: 'post',
                  url: `/admin/account/program/scheduler/status`,
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                context.commit('setProgramSchedulerVisibilityState', {
                    programsSchedulerEnabled: payload.status
                });

                successNotification(`Program Scheduler now ${payload.status ? 'enabled' : 'disabled'}`);

                return response.data;
            } catch ($e) {
                console.error('update Programs Scheduler error', $e.message);
            }
        },

        /**
         * Update Programs visibility
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateProgramsVisibility (context, payload) {
            try {
                const params = {
                    account_id: payload.accountId,
                    status: Number(payload.status)
                };

                //send as legacy form data
                const formData = buildFormData(params)

                const response = await axios({
                  method: 'post',
                  url: `/admin/account/program/visibility/status`,
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                context.commit('setProgramsVisibilityState', {
                    isVisibleToClients: payload.status
                });

                successNotification(`${payload.status ? 'Showing' : 'Hiding'} programs for account users`);

                return response.data;
            } catch ($e) {
                console.error('update Programs visibility error', $e.message);
            }
        },

        /**
         * Enable/disable programs for an account
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async enableAccountPrograms (context, payload) {
            try {
                const params = {
                    status: Number(payload.isEnabled),
                    account_id: payload.accountId
                };

                //send as legacy form data
                const formData = buildFormData(params)

                const response = await axios({
                  method: 'post',
                  url: `/admin/account/program/status`,
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                context.commit('setProgramsEnabledState', {
                    programsEnabled: payload.isEnabled
                });

                successNotification(`Programs now ${payload.isEnabled ? 'enabled' : 'disabled'}!`);

                return response.data;
            } catch ($e) {
                console.error('Enable Accounts Programs Enabled error', $e.message);
            }
        },

        /**
         * Update mfa setting
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateMfaSetting (context, payload) {
            try {
                const {accountId, isEnabled} = payload;

                //send as legacy form data
                const formData = buildFormData({
                  is_mfa_enabled: isEnabled ? 1 : 0
                })

                const response = await axios({
                  method: 'post',
                  url: `/admin/account/${accountId}/account-pref/mfa`,
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                context.commit('setSecuritySettings', {
                    mfaEnabled: isEnabled
                });

                successNotification(`Mandatory multi-factor authentification now ${isEnabled ? 'enabled' : 'disabled'}`);

                return response.data;
            } catch ($e) {
                console.error('update mfa settings error', $e.message);
            }
        },

        /**
         * Update email preferences
         *
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         * @return {Promise}
         */
        async updateEmailPreferences (context, payload) {
            try {
                const {emails, method, accountId} = payload;

                //@TODO - use this new route
                //const response = await axios.post(route_prefix + '/email-preferences/' + accountId, params);

                //send as legacy form data
                const formData = buildFormData({
                  'order-email-method': method,
                  'usergroup': parseInt(accountId),
                  'order-email-cc': emails.toString(),
                  'reset': false
                });

                const response = await axios({
                  method: 'post',
                  url: '/ajax/order_email_cc',
                  data: formData,
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                })

                if (response.data.error) {
                  failureNotification('Failed to update email preferences', response.data.error_msg);

                  return response.data;
                }

                context.commit('setEmailPreferences', {
                    emails: response.data.email.split(','),
                    method: response.data.method,
                });

                successNotification('Email preferences updated');

                return response.data;
            } catch ($e) {
                console.error('update email preferences error:', $e.message);
            }
        },

        /**
         * Send an invite to a user
         *
         * @param {Object} context {state, getters, commit}
         * @param user
         * @return {Promise}
         */
        async sendInvite (context, user) {
            try {
                let params = {
                    op: 'send',
                    uid: user.id
                };

                const response = await axios.get('/admin/view-invite', {params});

                return response.data;
            } catch ($e) {
                console.error('update email preferences error:', $e.message);
            }
        },

        /**
         * get the account users
         *
         * @param {Object} context {state, getters, commit}
         * @param user
         * @return {Promise}
         */
        async loadAccountUsers (context, accountId) {
            try {
                const response = await axios.get(`/admin/accounts/users?id=${accountId}`);

                context.commit('setAccountUsers', response.data);

                return response.data;
            } catch ($e) {
                console.error('get account users error:', $e.message);
            }
        },

        /**
         * Enable Program mandatory field for this account
         * @param {Object} context {state, getters, commit}
         * @param {Object} payload
         */
        async setAccountPreference(context, payload) {
            try {
                const { accountId, isProgramMandatory } = payload;

                const params = {
                    usergroup: accountId,
                    tag: 'programs',
                    subtag: 'mandatory',
                    value: isProgramMandatory
                };

                const response = await axios.post(`/admin/account/set-account-preference`, params);

                if (response && response.data && response.data.status) {
                    let message = isProgramMandatory ? "Enabled Program mandatory for account" : "Disabled Program mandatory for account";
                    successNotification(message);
                } else {
                    failureNotification('Failed to update account preference');
                }
            } catch ($e) {
                console.error('Failed to update account preference', $e.message);
            }
        },
    },
    getters: {

        /**
         * get Account
         */
        account (state) {
            return state.account;
        },

        /**
         * Get account users
         *
         * @param state
         * @returns {[]}
         */
        users (state) {
          return state.users.users;
        },

        /**
         * get translation review managment settings
         *
         * @param {Object} state
         * @return {Object}
         */
        trmSettings (state) {
            return state.translationReviewManagment;
        },

        /**
         * get security settings
         *
         * @param {Object} state
         * @return {Object}
         */
        securitySettings (state) {
            return state.security;
        },

        /**
         * get email notification settings
         *
         * @param {Object} state
         * @return {Object}
         */
        emailPreferences (state) {
            return state.emailPreferences;
        },

        /**
         * Get account Id
         *
         * @param state
         * @returns {number}
         */
        accountId (state) {
            return state.accountId;
        },

        /**
         * get the account kibana report settings
         *
         * @param {Object} state
         * @return {Array}
         */
        kibanaReportSettings (state) {
            return state.kibanaReports;
        },

        /**
         * get the account program settings
         *
         * @param {Object} state
         * @return {Array}
         */
        programSettings(state) {
            return state.programSettings;
        },

        /**
         * Get all teams sorted by priority.
         *
         * @param {Object} state
         * @return {Array}
         */
        sortedTeams(state) {
            return state.teams.sort((a, b) => a.priority - b.priority);
        },
    }
};
